import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Motif } from "./Motif";

const reviews = [
  {
    quote:
      "Afgelopen jaar hadden wij het geluk om jou aan onze zij te hebben. Jouw inzichten en steun waren voor heel ons gezin zo waardevol. Je leerde ons achter het gedrag van M. te kijken en hielp ons te zoeken naar wat hij echt nodig had om opnieuw te kunnen groeien. Dank je wel, voor alle inzichten, je steun en je betrokkenheid. Dank je wel om opnieuw rust te brengen in ons gezin.",
    author: "Mama van M.",
  },
  {
    quote:
      "De begeleiding heeft me echt geholpen. Ik kreeg meer inzicht in wie ik ben als persoon en ook als mama. Ik leerde om mijn eigen spanning vroeger te herkennen en zaken op een andere manier te communiceren. Ik voel nu veel meer rust.",
    author: "L.",
  },
  {
    quote:
      "De stap naar hulp was groot voor ons. Griet stelde ons snel op ons gemak. Ze leerde ons om ook te kijken naar de positieve dingen die er nog zijn, en niet enkel naar het negatieve gedrag van onze zoon. Samen leerden we om dingen bespreekbaar te maken en terug te luisteren naar elkaar. Vandaag zijn we een jaar verder en helemaal op goeie weg.",
    author: "Mama en papa van L.",
  },
  {
    quote:
      "We zijn zo dankbaar voor de ondersteuning die R. kreeg. Hij leerde hoe zijn hoofdje werkte en kreeg handvatten die hem helpen om met zijn boosheid om te gaan.",
    author: "Ouders van R.",
  },
];

export const Reviews = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  const go = useCallback((d) => {
    setDir(d);
    setIdx((p) => (p + d + reviews.length) % reviews.length);
  }, []);

  useEffect(() => {
    const t = setInterval(() => go(1), 40000);
    return () => clearInterval(t);
  }, [go]);

  return (
    <section className="max-w-4xl mx-auto px-5 md:px-8 pt-6 md:pt-20 pb-16 md:pb-28">
      <div className="relative overflow-hidden bg-[#E7E4D9] rounded-[28px] md:rounded-[40px] px-6 md:px-14 py-8 md:py-14 shadow-[0_30px_80px_rgba(79,80,72,0.13)]">
        <div className="pointer-events-none absolute -right-20 -bottom-20 w-[280px] h-[280px]">
          <Motif opacity={0.18} />
        </div>
        <div className="pointer-events-none absolute -left-12 -top-12 w-[180px] h-[180px]">
          <Motif opacity={0.12} flip />
        </div>

        <Quote className="mx-auto text-kern-terracotta mb-3 md:mb-5 w-7 h-7 md:w-10 md:h-10" fill="#C99B87" strokeWidth={0} />

        <div className="relative min-h-[110px] md:min-h-[130px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-center max-w-2xl mx-auto"
              data-testid={`review-${idx}`}
            >
              <p className="font-serif text-base md:text-2xl leading-snug text-kern-dark italic">
                {reviews[idx].quote}
              </p>
              <p className="mt-4 md:mt-6 text-xs md:text-sm tracking-[0.18em] uppercase text-kern-taupe">
                — {reviews[idx].author}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="relative flex items-center justify-center gap-4 md:gap-6 mt-6 md:mt-8">
          <button data-testid="review-prev" onClick={() => go(-1)} aria-label="Vorige"
            className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-kern-olive transition-colors duration-300">
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {reviews.map((_, i) => (
              <button
                key={i}
                data-testid={`review-dot-${i}`}
                onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }}
                aria-label={`Review ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-kern-terracotta" : "w-2 bg-kern-taupe/40"}`}
              />
            ))}
          </div>
          <button data-testid="review-next" onClick={() => go(1)} aria-label="Volgende"
            className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-kern-olive transition-colors duration-300">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
